import React, { useEffect, useState } from 'react';
import axios from 'axios';
// material
import { 
  Box, Button, Container, FormControl, FormHelperText, Grid, Modal, Stack, TextField, Typography,
  Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
// components
import Iconify from '../components/Iconify';
import Page from '../components/Page';
import { BlogPostCard, BlogPostsSearch, BlogPostsSort } from '../sections/@dashboard/blog'; // Ensure these exist
import { useTheme } from '@mui/material/styles';

 // Add this at the top of your component


// ----------------------------------------------------------------------

const SORT_OPTIONS = [
  { value: 'latest', label: 'Latest' },
  { value: 'popular', label: 'Popular' },
  { value: 'oldest', label: 'Oldest' },
];

// Alert helper for Snackbar
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ----------------------------------------------------------------------

export default function Blog() {
  const [openModal, setOpenModal] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    image: null,
    url: '',
  });
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [openOverlay, setOpenOverlay] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');// 'success' | 'error' | 'warning' | 'info'
  const theme = useTheme();

  useEffect(() => {
    axios.get('https://ipms.insollex.com/api/post')
      .then(response => {
        const postsData = response.data;
        setPosts(postsData);
        setFilteredPosts(postsData);
      })
      .catch(error => {
        console.error('Error fetching blog posts:', error);
        showSnackbar('Failed to fetch posts.', 'error');
      });
  }, []);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPost((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPost((prevState) => ({
        ...prevState,
        image: file,
      }));
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSubmit = () => {
    if (!newPost.title.trim() || !newPost.content.trim()) {
      showSnackbar('Title and content are required.', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('title', newPost.title);
    formData.append('content', newPost.content);
    formData.append('url', newPost.url);
    if (newPost.image) formData.append('image', newPost.image);

    setIsSubmitting(true);
    axios.post('https://ipms.insollex.com/api/post', formData)
      .then(response => {
        const createdPost = response.data;
        setPosts(prev => [...prev, createdPost]);
        setFilteredPosts(prev => [...prev, createdPost]);
        handleCloseModal();
        setNewPost({ title: '', content: '', image: null, url: '' });
        showSnackbar('Post submitted successfully!', 'success');
      })
      .catch(error => {
        console.error('Error submitting post:', error);
        showSnackbar('Error submitting post.', 'error');
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query === '') {
      setFilteredPosts(posts);
    } else {
      const filtered = posts.filter(post =>
        post.title.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredPosts(filtered);
    }
  };

  const handleSort = (option) => {
    let sortedPosts = [...filteredPosts];
    if (option === 'latest') {
      sortedPosts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (option === 'oldest') {
      sortedPosts.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (option === 'popular') {
      sortedPosts.sort((a, b) => b.view - a.view);
    }
    setFilteredPosts(sortedPosts);
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
    setOpenOverlay(true);
  };

  const incrementViewCount = async (postId) => {
    try {
      await axios.patch(`https://ipms.insollex.com/api/post/${postId}/view`);
      setPosts(posts.map(post =>
        post._id === postId ? { ...post, view: (post.view || 0) + 1 } : post
      ));
    } catch (error) {
      console.error('Error incrementing view count:', error);
      showSnackbar('Failed to increment view count.', 'error');
    }
  };

  const incrementShareCount = async (postId) => {
    try {
      await axios.patch(`https://ipms.insollex.com/api/post/${postId}/share`);
      setPosts(posts.map(post =>
        post._id === postId ? { ...post, share: (post.share || 0) + 1 } : post
      ));
    } catch (error) {
      console.error('Error incrementing share count:', error);
      showSnackbar('Failed to increment share count.', 'error');
    }
  };

  const handleDelete = (postId) => {
    axios.delete(`https://ipms.insollex.com/api/spost/${postId}`)
      .then(response => {
        setPosts(posts.filter(post => post._id !== postId));
        setFilteredPosts(filteredPosts.filter(post => post._id !== postId));
        setOpenOverlay(false);
        showSnackbar('Post deleted successfully.', 'success');
      })
      .catch(error => {
        console.error('Error deleting post:', error);
        showSnackbar('Error deleting post.', 'error');
      });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const isValidUrl = (url) => {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <Page title="Dashboard: Blog">
      <Container maxWidth={false}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Blog
          </Typography>
          <Button variant="contained" onClick={handleOpenModal} startIcon={<Iconify icon="eva:plus-fill" />}>
            New Post
          </Button>
        </Stack>

        <Stack mb={5} direction="row" alignItems="center" justifyContent="space-between">
          <BlogPostsSearch posts={posts} query={searchQuery} onSearch={handleSearch} />
          <BlogPostsSort options={SORT_OPTIONS} onSort={handleSort} />
        </Stack>

        <Grid container spacing={3}>
          {filteredPosts.map((post) => (
            <BlogPostCard
              key={post._id || post.title}
              post={post}
              onClick={() => handlePostClick(post)}
              onView={() => incrementViewCount(post._id)}
              onShare={() => incrementShareCount(post._id)}
            />
          ))}
        </Grid>
      </Container>

      <Modal open={openModal} onClose={handleCloseModal}>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      bgcolor: 'background.paper', // Theme-aware background
      color: 'text.primary',        // Theme-aware text color
      padding: 4,
      width: '400px',
      borderRadius: 2,
      boxShadow: 24,
    }}
  >
    <Typography variant="h6" gutterBottom>
      Create New Post
    </Typography>
    <TextField
      fullWidth
      label="Title"
      name="title"
      value={newPost.title}
      onChange={handleInputChange}
      margin="normal"
      InputLabelProps={{ style: { color: theme.palette.text.primary } }}
    />
    <TextField
      fullWidth
      label="Content"
      name="content"
      value={newPost.content}
      onChange={handleInputChange}
      multiline
      rows={4}
      margin="normal"
      InputLabelProps={{ style: { color: theme.palette.text.primary } }}
    />
    <TextField
      fullWidth
      label="URL"
      name="url"
      value={newPost.url}
      onChange={handleInputChange}
      margin="normal"
      sx={{ mb: 2 }}
      InputLabelProps={{ style: { color: theme.palette.text.primary } }}
    />

    <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
      <Button variant="outlined" component="label" fullWidth>
        {newPost.image ? 'Change Image' : 'Upload Image'}
        <input
          type="file"
          name="image"
          onChange={handleImageChange}
          accept="image/*"
          hidden
        />
      </Button>
      <FormHelperText sx={{ color: theme.palette.text.secondary }}>
        Upload an image (optional)
      </FormHelperText>
    </FormControl>

    {newPost.image && (
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="caption">Preview:</Typography>
        <img
          src={URL.createObjectURL(newPost.image)}
          alt="preview"
          style={{ width: '100%', borderRadius: 8 }}
        />
      </Box>
    )}

    <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
      <Button onClick={handleCloseModal}>Cancel</Button>
      <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </Button>
    </Stack>
  </Box>
</Modal>


      {/* Overlay Modal for Viewing Full Post Details */}
      <Modal open={openOverlay} onClose={() => setOpenOverlay(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: 4,
            width: '600px',
            borderRadius: 2,
            maxHeight: '80%',
            overflowY: 'auto',
          }}
        >
          {selectedPost ? (
            <>
              <Typography variant="h4" gutterBottom>
                {selectedPost.title}
              </Typography>

              <Typography variant="body1" paragraph>
                {selectedPost.content}
              </Typography>

              {selectedPost.image && (
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    p: 1,
                    mb: 2,
                    boxShadow: 1,
                    backgroundColor: '#f9f9f9',
                  }}
                >
                  <img
                    src={`https://ipms.insollex.com${selectedPost.image}`}
                    alt={selectedPost.title}
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                </Box>
              )}

              <Typography variant="body2" color="text.secondary">
                URL:{' '}
                {selectedPost.url ? (
                  <a
                    href={
                      selectedPost.url.trim().startsWith('http://') ||
                      selectedPost.url.trim().startsWith('https://')
                        ? selectedPost.url.trim()
                        : `https://${selectedPost.url.trim()}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selectedPost.url}
                  </a>
                ) : (
                  'N/A'
                )}
              </Typography>

              <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
                <Button onClick={() => setOpenOverlay(false)}>Close</Button>
                <Button color="error" onClick={() => handleDelete(selectedPost._id)}>
                  Delete
                </Button>
              </Stack>
            </>
          ) : (
            <Typography variant="h6">Loading...</Typography>
          )}
        </Box>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Page>
  );
}
